// src/components/AboutSection.jsx
import React from 'react';
import {
  ShieldCheck,
  ArrowRight,
  Users,
  Info,
  MapPin,
  Building2,
  FileText,
  CalendarClock,
  BadgeCheck,
  Star
} from 'lucide-react';

export default function AboutSection() {
  return (
    <>
      <style>{`
        :root{
          --brand:#1e3a8a;
          --brand-2:#0ea5e9;
          --ink:#0f172a;
          --muted:#475569;
          --line:#e2e8f0;
          --paper:#ffffff;
          --bg:#f8fafc;
          --chip:#f1f5f9;
        }
        .about-section{
          position:relative; isolation:isolate;
          overflow:hidden;
          background: radial-gradient(900px 420px at 7% -10%, #e7f5ff 0%, rgba(231,245,255,0) 60%), var(--bg);
          padding:clamp(3rem,6vw,6rem) 1rem;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        }
        .about-container{ max-width:1200px; margin:0 auto; }
        .blob{ position:absolute; inset:auto auto 5% -8%; width:min(420px, 80vw); height:min(420px, 80vw); border-radius:50%;
          background:radial-gradient(circle at 30% 30%, #dbeafe 0%, transparent 60%); filter:blur(28px); opacity:.6; z-index:-1;}
        .blob2{ position:absolute; inset:-12% -10% auto auto; width:min(460px, 85vw); height:min(460px, 85vw); border-radius:50%;
          background:radial-gradient(circle at 70% 40%, #fee2e2 0%, transparent 60%); filter:blur(28px); opacity:.5; z-index:-1;}
        .about-row{ display:grid; gap:clamp(1.5rem,3vw,3rem); grid-template-columns:1fr; align-items:center; }
        @media (min-width:1024px){ .about-row{ grid-template-columns: 320px 1fr; } }
        .about-icon-wrap{ position:relative; display:flex; justify-content:center; }
        .orb{
          position:relative; width:min(78vw, 320px); height:min(78vw, 320px); border-radius:50%;
          background: conic-gradient(from 180deg at 50% 50%, rgba(14,165,233,.15), rgba(30,58,138,.18), rgba(14,165,233,.15));
          box-shadow: 0 20px 50px rgba(2,6,23,.12), inset 0 0 0 1px rgba(255,255,255,.7);
          display:flex; align-items:center; justify-content:center;
        }
        .orb::after{
          content:""; position:absolute; inset:18px; border-radius:50%;
          background:linear-gradient(180deg, rgba(255,255,255,.85), rgba(255,255,255,.65));
          box-shadow: inset 0 0 24px rgba(2,6,23,.06);
        }
        .orb-icon{
          position:relative; z-index:1; display:flex; align-items:center; justify-content:center;
          width:120px; height:120px; border-radius:50%;
          background: var(--paper);
          border:1px solid rgba(226,232,240,.6);
          box-shadow: 0 10px 28px rgba(2,6,23,.08);
        }
        .eyebrow{
          display:inline-flex; align-items:center; gap:.5rem;
          background:var(--paper); border:1px solid var(--line); color:var(--ink);
          padding:.4rem .75rem; border-radius:999px; font-weight:800; letter-spacing:.12em; text-transform:uppercase;
        }
        .about-title{ margin:.6rem 0 0; color:var(--ink); font-size:clamp(1.8rem,3.5vw,2.6rem); font-weight:900; line-height:1.1;}
        .about-sub{ margin:.75rem 0 1.25rem; color:var(--muted); font-size:1.05rem; max-width:60ch;}
        .chips{ display:flex; flex-wrap:wrap; gap:.65rem; margin:.5rem 0 1.5rem;}
        .chip{
          display:inline-flex; align-items:center; gap:.45rem; padding:.45rem .65rem; border-radius:10px;
          background:var(--chip); border:1px solid var(--line); color:var(--ink); font-weight:700; font-size:.82rem;
        }
        .chip .star{ color:#f59e0b; }
        .snapshot{
          background:var(--paper); border:1px solid var(--line); border-radius:16px; padding:1rem;
          box-shadow: 0 10px 28px rgba(2,6,23,.06);
        }
        .snap-grid{ display:grid; grid-template-columns:1fr; gap:.6rem .75rem; }
        @media (min-width:640px){ .snap-grid{ grid-template-columns: 240px 1fr; } }
        .dt{ color:#334155; font-weight:800; }
        .dd{ color:#0f172a; }
        .values{ display:grid; gap:.8rem; margin:1rem 0; }
        .val{ display:flex; align-items:center; gap:.6rem; color:var(--ink); }
        .val svg{ color:#16a34a; }
        .links{ display:flex; gap:1rem; flex-wrap:wrap; margin-top:1rem;}
        .link{
          display:inline-flex; align-items:center; gap:.45rem; text-decoration:none; font-weight:800;
          color:var(--brand); border:1px solid var(--line); background:var(--paper);
          padding:.6rem .9rem; border-radius:10px; transition:transform .2s ease, box-shadow .2s ease, color .2s ease;
        }
        .link:hover{ transform:translateY(-1px); box-shadow:0 10px 22px rgba(30,58,138,.15); color:#173074; }
        .timeline{ display:flex; gap:1.25rem; align-items:center; flex-wrap:wrap; margin-top:1.25rem; }
        .tl{
          --c: var(--brand);
          position:relative; padding:.5rem .75rem; border-radius:999px; background:#eef2ff; color:#1e293b; font-weight:800; font-size:.85rem;
          display:inline-flex; align-items:center; gap:.5rem; border:1px solid #dbeafe;
        }
        .tl .dot{ width:8px; height:8px; border-radius:50%; background:var(--c); box-shadow:0 0 0 3px rgba(30,58,138,.15);}
      `}</style>

      <section className="about-section" aria-label="About MRM Lettings Property & Management Ltd">
        <div className="blob" />
        <div className="blob2" />

        <div className="about-container">
          <div className="about-row">
            {/* LEFT: brand orb */}
            <div className="about-icon-wrap">
              <div className="orb">
                <div className="orb-icon" aria-hidden="true">
                  <ShieldCheck size={56} color="#1e293b" />
                </div>
              </div>
            </div>

            {/* RIGHT: content */}
            <div>
              <span className="eyebrow">
                <Info size={16} /> About MRM Lettings
              </span>
              <h2 className="about-title">Property &amp; Management Ltd</h2>
              <p className="about-sub">
                One dedicated Middlesbrough office with local experts and local knowledge. We manage and
                market homes with a focus on transparent communication and dependable service for buyers,
                sellers, landlords and tenants across Teesside.
              </p>

              {/* trust chips */}
              <div className="chips">
                <span className="chip"><Star size={16} className="star" /> 4.74 / 5 · 1,571 Google Reviews</span>
                <span className="chip"><MapPin size={16} /> 142 Victoria Road, Middlesbrough, TS1 3HU</span>
                <span className="chip"><Building2 size={16} /> SIC 68320</span>
              </div>

              {/* values */}
              <div className="values" role="list">
                <div className="val" role="listitem"><BadgeCheck size={18}/> Straightforward guidance &amp; clear updates</div>
                <div className="val" role="listitem"><BadgeCheck size={18}/> Proactive tenant &amp; landlord support</div>
                <div className="val" role="listitem"><BadgeCheck size={18}/> Local market insight you can trust</div>
              </div>

              {/* company snapshot */}
              <div className="snapshot" aria-labelledby="company-snapshot">
                <div className="chips" style={{ marginTop: 0, marginBottom: '.5rem' }}>
                  <span className="chip"><Users size={16}/> Private limited company</span>
                  <span className="chip"><BadgeCheck size={16}/> Status: Active</span>
                </div>

                {/* FIXED: style now an object, not a string */}
                <h3
                  id="company-snapshot"
                  style={{ margin: '0 0 .5rem', fontSize: '1.05rem', color: '#0f172a' }}
                >
                  Company snapshot
                </h3>

                <div className="snap-grid">
                  <div className="dt">Registered office address</div>
                  <div className="dd">142 Victoria Road, Middlesbrough, England, TS1 3HU</div>

                  <div className="dt">Incorporated on</div>
                  <div className="dd">15 April 2019</div>

                  <div className="dt">Accounts</div>
                  <div className="dd">
                    Next accounts made up to 30 April 2026 — due by 31 January 2027<br/>
                    Last accounts made up to 30 April 2025
                  </div>

                  <div className="dt">Confirmation statement</div>
                  <div className="dd">
                    Next statement date 14 April 2026 — due by 28 April 2026<br/>
                    Last statement dated 14 April 2025
                  </div>
                </div>

                {/* mini timeline */}
                <div className="timeline" aria-label="Company timeline">
                  <span className="tl"><span className="dot" aria-hidden="true"></span> 2019 · Incorporated</span>
                  <span className="tl" style={{ '--c': '#0ea5e9' }}><span className="dot" aria-hidden="true"></span> 2025 · Latest accounts filed</span>
                  <span className="tl"><span className="dot" aria-hidden="true"></span> 2026 · Next accounts &amp; statement due</span>
                </div>
              </div>

              {/* helpful links */}
              <div className="links" role="navigation" aria-label="About links">
                <a className="link" href="/about">More about us <ArrowRight size={16}/></a>
                <a className="link" href="/#team">Meet our team <ArrowRight size={16}/></a>
                <a className="link" href="/contact">Contact us <ArrowRight size={16}/></a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
